// src/components/EquipmentTable.jsx
import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Input } from "./ui/input";
import { Search } from "lucide-react";
import CartCounter from './CartCounter';
import { useAuth } from '../context/AuthContext';
import debounce from 'lodash/debounce';

// Helper functions remain the same
const sortFolders = (folders = []) => {
  return [...folders].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const sortItems = (items = []) => {
  return [...items].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
};

const EquipmentTable = ({ data = { tree: [] }, scrollToId = null, height }) => {
  const { currentUserProfile } = useAuth();
  const isApproved = currentUserProfile?.approvalStatus === 'approved';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const virtuosoRef = useRef(null);

  // State for the top-most sticky header
  const [currentStickyHeader, setCurrentStickyHeader] = useState(null);
  const [isAtTop, setIsAtTop] = useState(true);


  // Sorting and filtering logic remains the same
  const sortedData = useMemo(() => {
    if (!data?.tree) return { tree: [] };
    const sortFolder = (folder) => ({
      ...folder,
      items: sortItems(folder.items),
      children: sortFolders(folder.children?.map(sortFolder) || []),
    });
    return {
      tree: sortFolders(data.tree.map(sortFolder))
    };
  }, [data]);

  const debouncedSetSearch = useCallback(
    debounce((value) => setDebouncedSearchQuery(value), 150),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchQuery);
    return () => debouncedSetSearch.cancel();
  }, [searchQuery, debouncedSetSearch]);

  const filteredData = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return sortedData;
    }
    const query = debouncedSearchQuery.toLowerCase().trim();
    const filterFolder = (folder) => {
      const folderMatches = folder.name.toLowerCase().includes(query);
      const filteredItems = folder.items?.filter(item => 
        item.name.toLowerCase().includes(query)
      ) || [];
      const filteredChildren = folder.children?.map(child => filterFolder(child)).filter(Boolean) || [];
      
      if (folderMatches || filteredItems.length > 0 || filteredChildren.length > 0) {
        return {
          ...folder,
          items: sortItems(filteredItems),
          children: sortFolders(filteredChildren)
        };
      }
      return null;
    };
    return {
      tree: sortFolders(data.tree.map(folder => filterFolder(folder)).filter(Boolean))
    };
  }, [data, debouncedSearchQuery, sortedData]);

  // Data preparation logic remains the same
  const { flattenedList, folderMap } = useMemo(() => {
    const list = [];
    const map = new Map();

    const hasContentRecursively = (folder) => {
      if (!folder) return false;
      if (folder.items && folder.items.length > 0) return true;
      if (!folder.children || folder.children.length === 0) return false;
      return folder.children.some(hasContentRecursively);
    };

    const processFolder = (folder, rootFolderName = null) => {
      if (!hasContentRecursively(folder)) {
        return;
      }
      const resolvedRootFolderName = rootFolderName || folder.name || 'Uncategorized';
      map.set(folder.id, list.length);
      list.push({ _type: 'HEADER', ...folder });
      if (folder.items && folder.items.length > 0) {
        folder.items.forEach(item => {
          const providedDefaultGroup = item.defaultGroup ?? item.defaultgroup ?? item.default_group ?? null;
          const resolvedDefaultGroupName =
            (typeof providedDefaultGroup === 'string' && providedDefaultGroup.trim()) ||
            providedDefaultGroup?.name ||
            providedDefaultGroup?.displayname ||
            providedDefaultGroup?.displayName ||
            'Uncategorized';

          list.push({
            _type: 'ITEM',
            ...item,
            defaultgroup: resolvedDefaultGroupName,
            immediateFolderName: folder.name || 'Uncategorized',
            rootFolderName: resolvedRootFolderName,
          });
        });
      }
      if (folder.children && folder.children.length > 0) {
        folder.children.forEach((child) => processFolder(child, resolvedRootFolderName));
      }
    };

    if (filteredData?.tree) {
        filteredData.tree.forEach(processFolder);
    }
    return { flattenedList: list, folderMap: map };
  }, [filteredData]);

  // Scrolling logic remains the same
  const getScrollIndex = useCallback((folderId) => {
    return folderMap.get(folderId) ?? 0;
  }, [folderMap]);

  useEffect(() => {
    if (scrollToId && virtuosoRef.current) {
      const scrollIndex = getScrollIndex(scrollToId);
      setTimeout(() => {
        virtuosoRef.current.scrollToIndex({
          index: scrollIndex,
          align: 'start',
          behavior: 'auto',
          offset: 40
        });
      }, 10);
    }
  }, [scrollToId, getScrollIndex]);
  
  // Header update logic remains the same
  const handleRangeChanged = useCallback(({ startIndex }) => {
    let lastHeader = null;
    for (let i = startIndex; i >= 0; i--) {
      const entry = flattenedList[i];
      if (entry?._type === 'HEADER') {
        lastHeader = entry;
        break;
      }
    }
    setCurrentStickyHeader(lastHeader);
  }, [flattenedList]);

  // --- Refactored: Always render search bar and header ---
  return (
    <div className="relative w-full max-w-[976px] flex flex-col" style={{ height: height || '80vh' }}>
      {/* Static headers */}
      <div className="flex-shrink-0">
        <div className="relative mb-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={searchInputRef}
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 pr-8 border border-border"
            style={{ boxShadow: 'none' }}
            autoComplete="off"
            onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); searchInputRef.current?.blur(); } }}
          />
          {searchQuery && (<button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted focus:bg-muted focus:outline-none" tabIndex={0}><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>)}
        </div>
      </div>
      {/* --- THIS IS A CHANGE: Increased height of static table header --- */}
      <div className="bg-background border-b border-border flex-shrink-0">
        <div className="h-10 flex items-center">
          <div className="w-[60px] md:w-[80px] px-2 text-foreground">Qty</div>
          <div className="flex-1 px-2 text-foreground">Name</div>
          {isApproved && <div className="w-[80px] md:w-[100px] px-2 text-foreground text-right">Price</div>}
          {!isApproved && <div className="w-[80px] md:w-[100px]" />}
        </div>
      </div>

      {/* If no results, show message below search bar and header */}
      {flattenedList.length === 0 ? (
        <div className="p-4">
          {searchQuery ? 'No equipment found for your search.' : 'No equipment data to display.'}
        </div>
      ) : (
        <>
          {/* External sticky header */}
          {/* --- THIS IS A CHANGE: Increased height and adjusted sticky top position --- */}
          {currentStickyHeader && !isAtTop && (
            <div className="sticky top-[88px] z-20 font-semibold bg-[#20292e] hover:bg-[#2a353c] border-b border-border h-10 flex items-center px-2 flex-shrink-0">
              <span className="truncate">{currentStickyHeader.name}</span>
            </div>
          )}

          {/* --- THIS IS A CHANGE: Restructured the scrolling container for proper sticky behavior --- */}
          <div className="w-full flex-1 relative">
            <Virtuoso
              ref={virtuosoRef}
              style={{ height: '100%' }}
              // --- THIS IS A CHANGE: Updated fixed item height ---
              fixedItemHeight={40}
              totalCount={flattenedList.length}
              rangeChanged={handleRangeChanged}
              atTopStateChange={setIsAtTop}
              itemContent={index => {
                const entry = flattenedList[index];
                if (entry._type === 'HEADER') {
                  // --- THIS IS A CHANGE: Increased height of in-list header ---
                  return (
                    <div
                      key={`folder-${entry.id}`}
                      id={`folder-${entry.id}`}
                      className="font-semibold bg-[#20292e] hover:bg-[#2a353c] border-b border-border h-10 flex items-center px-2"
                    >
                      <span className="truncate">{entry.name}</span>
                    </div>
                  );
                } else {
                  const item = entry;
                  // --- THIS IS A CHANGE: Increased height and enhanced hover effect ---
                  return (
                    <div
                      key={`item-${item.id}`}
                      className="h-10 bg-background hover:bg-yellow-50 border-b border-border flex items-center hover:text-primary-foreground"
                    >
                      <div className="w-[60px] md:w-[80px] px-2">
                        <CartCounter item={item} />
                      </div>
                      <div className="flex-1 px-2 truncate">
                        <span className="truncate">{item.name}</span>
                      </div>
                      {isApproved && (
                        <div className="w-[80px] md:w-[100px] px-2 text-right truncate">
                          {item.price}
                        </div>
                      )}
                      {!isApproved && <div className="w-[80px] md:w-[100px]" />}
                    </div>
                  );
                }
              }}
              overscan={200}
              components={{ Footer: () => <div style={{ height: '20px' }} /> }}
              context={{ isApproved }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EquipmentTable;
